import { Component, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { BrandingService } from '@next/core-lib/branding';
import { NavigationActions, ParentNavigationItem } from '@next/core-lib/navigation';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { AccessRoleTypes, UserSelectors } from '@next/core-lib/user';
import {
  WrapperSiteSidenavActions,
  WrapperSiteSidenavComponent,
} from '@next/core-lib/wrapper-site-sidenav';
import { Store } from '@ngrx/store';
import { combineLatest, skipWhile, take } from 'rxjs';

import { ApplicationKeys } from './shared/models/shared.model';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [WrapperSiteSidenavComponent, RouterOutlet],
})
export class AppComponent {
  private store = inject<Store<RootState>>(Store);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly brandingService = inject(BrandingService);

  logoSvgIcon: string = undefined;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    combineLatest([
      this.store.select(UserSelectors.selectUserApplications),
      this.store.select(AuthorizationSelectors.hasAtLeastAdministratorRole),
    ])
      .pipe(skipWhile(([accessRules]) => !accessRules))
      .subscribe(([accessRules, isAtLeastAdmin]) => {
        const menuItems: ParentNavigationItem[] = [
          {
            relativeUrl: '/cycle',
            label: 'app.cycles',
            icon: 'loop',
          },
          {
            relativeUrl: '/report',
            label: 'app.reports',
            icon: 'assignment',
          },
          {
            label: 'app.advices',
            icon: 'library_books',
            children: [
              {
                label: 'app.reference-value-sheet',
                relativeUrl: '/reference-value-sheet',
              },
              {
                label: 'app.feed-advice',
                relativeUrl: '/feed-advice',
              },
            ],
          },
          {
            relativeUrl: '/farmer-insights',
            label: 'app.farmer-insights',
            icon: 'assessment',
          },
          {
            relativeUrl: '/within-cycle-performance',
            label: 'app.within-cycle-performance',
            icon: 'assessment',
          },
        ];

        if (!this.brandingService.isTenant('Agravis')) {
          menuItems.splice(1, 0, {
            relativeUrl: '/delivery',
            label: 'app.deliveries',
            icon: 'local_shipping',
          });
        }

        if (isAtLeastAdmin) {
          menuItems.push({
            label: 'app.management',
            icon: 'tune',
            children: [
              {
                relativeUrl: '/product',
                label: 'app.products',
              },
              {
                relativeUrl: '/standard-reference-value-sheet',
                label: 'app.standard-reference-value-sheet',
              },
              {
                relativeUrl: '/reference-scheme',
                label: 'app.reference-scheme',
              },
            ],
          });
        }

        menuItems.push({
          relativeUrl: '/settings',
          label: 'app.settings',
          icon: 'settings',
        });

        const showSustainability = accessRules.some(
          (rule) =>
            rule.ApplicationKey === ApplicationKeys.PoultryNextSustainability &&
            rule.RoleKey !== AccessRoleTypes.NotAuthorized,
        );
        if (showSustainability && !this.brandingService.isTenant('Agravis')) {
          menuItems.splice(1, 0, {
            label: 'sustainability.sustainability',
            icon: 'eco',
            children: [
              {
                relativeUrl: '/farm-resources',
                label: 'sustainability.farm-resources',
              },
              {
                relativeUrl: '/carbon-footprint',
                label: 'sustainability.carbon-footprint',
              },
            ],
          });
        }

        this.store.dispatch(NavigationActions.setNavigationItems({ navigationItems: menuItems }));
      });
  }

  closeSideNav() {
    this.breakpoint.ltLg$.pipe(take(1)).subscribe((isMobile) => {
      if (isMobile) {
        this.store.dispatch(WrapperSiteSidenavActions.closeSidenav());
      }
    });
  }
}
